import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Tooltip,
  Typography,
} from '@mui/joy';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/joy/styles';

import { fetchCongregationAdminApplications, fetchCongregationAdminPipeline, fetchCongregationMasters } from '../../api';
import type { CongregationAdminFilter } from '../../api';
import type {
  CongregationApplication,
  CongregationApplicationPipeline,
  CongregationApplicationStatus,
  CongregationMastersPayload,
} from '../../types/congregation';
import { useCongregationAccess } from '../../congregation/context/CongregationAccessContext';
import {
  ActivityTimeline,
  type ActivityTimelineItem,
} from '../../congregation/components/ActivityTimeline';
import { normalizeMastersPayload } from '../../congregation/utils';
import { useSnackbarStore } from '../../store/useSnackbarStore';

dayjs.extend(relativeTime);

const statusLabelMap: Record<CongregationApplicationStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  ASSIGNED_TO_MENTOR: 'Assigned to caretaker',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn',
};

const statusColorMap: Record<
  CongregationApplicationStatus,
  'neutral' | 'primary' | 'success' | 'danger' | 'warning'
> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  ASSIGNED_TO_MENTOR: 'primary',
  ACCEPTED: 'success',
  REJECTED: 'danger',
  WITHDRAWN: 'neutral',
};

const statusOptions = [
  { value: '' as const, label: 'All statuses' },
  { value: 'SUBMITTED' as const, label: statusLabelMap.SUBMITTED },
  { value: 'ASSIGNED_TO_MENTOR' as const, label: statusLabelMap.ASSIGNED_TO_MENTOR },
  { value: 'PENDING' as const, label: 'Pending (submitted + assigned)' },
  { value: 'ACCEPTED' as const, label: statusLabelMap.ACCEPTED },
  { value: 'REJECTED' as const, label: statusLabelMap.REJECTED },
  { value: 'WITHDRAWN' as const, label: statusLabelMap.WITHDRAWN },
] as const satisfies ReadonlyArray<{
  value: '' | CongregationApplicationStatus | 'PENDING';
  label: string;
}>;

type StatusOptionValue = (typeof statusOptions)[number]['value'];

const fallbackActivityLimit = 12;

const formatActionLabel = (value: string) =>
  value
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0) + segment.slice(1).toLowerCase())
    .join(' ');

const getStatusLabel = (status: CongregationApplicationStatus) =>
  statusLabelMap[status] ?? formatActionLabel(status);

export default function CongregationDashboardPage() {
  const { membership } = useCongregationAccess();
  const { showSnackbar } = useSnackbarStore();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const heroBackground = isDark
    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)'
    : 'radial-gradient(120% 140% at 8% 0%, rgba(255, 218, 170, 0.2) 0%, rgba(255, 218, 170, 0) 55%), radial-gradient(120% 140% at 95% 5%, rgba(165, 197, 255, 0.2) 0%, rgba(165, 197, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(248, 250, 255, 1) 45%, rgba(244, 246, 252, 1) 100%)';
  const heroOverlay = isDark
    ? 'radial-gradient(120% 120% at 15% -10%, rgba(56, 189, 248, 0.14), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(251, 191, 36, 0.12), transparent 55%)'
    : 'radial-gradient(120% 120% at 15% -10%, rgba(255, 220, 160, 0.45), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(129, 158, 255, 0.28), transparent 55%)';
  const heroTitle = isDark ? 'rgba(248,250,252,0.94)' : 'text.primary';
  const heroBody = isDark ? 'rgba(226,232,240,0.8)' : 'text.secondary';
  const panelWarm = isDark
    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))'
    : 'linear-gradient(135deg, rgba(255, 237, 213, 0.9), rgba(255, 247, 231, 0.9))';
  const panelCool = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.95))'
    : 'linear-gradient(135deg, rgba(224, 243, 255, 0.8), rgba(241, 249, 255, 0.95))';
  const orbColor = isDark ? 'rgba(56, 189, 248, 0.18)' : 'rgba(255, 214, 153, 0.35)';
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = React.useState(true);
  const [applications, setApplications] = React.useState<CongregationApplication[]>([]);
  const [pipeline, setPipeline] = React.useState<CongregationApplicationPipeline | null>(null);
  const [masters, setMasters] = React.useState<CongregationMastersPayload | null>(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const firstLoadRef = React.useRef(true);
  const [activityEvents, setActivityEvents] = React.useState<ActivityTimelineItem[]>([]);
  const pipelineSummary = React.useMemo(() => {
    return {
      pending: pipeline?.pending ?? (pipeline?.submitted ?? 0) + (pipeline?.assigned ?? 0),
      submitted: pipeline?.submitted ?? 0,
      assigned: pipeline?.assigned ?? 0,
      accepted: pipeline?.accepted ?? 0,
      rejected: pipeline?.rejected ?? 0,
      withdrawn: pipeline?.withdrawn ?? 0,
    };
  }, [pipeline]);

  const statusParam = searchParams.get('status');
  const statusFilter: StatusOptionValue =
    statusParam && statusOptions.some((option) => option.value === statusParam)
      ? (statusParam as StatusOptionValue)
      : '';

  const updateSearchParam = React.useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (value && value.length > 0) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const loadMasters = React.useCallback(async () => {
    try {
      const data = await fetchCongregationMasters();
      setMasters(normalizeMastersPayload(data));
    } catch (error) {
      console.error('Failed to load congregation masters', error);
      showSnackbar('Unable to load congregation master data.', 'warning');
    }
  }, [showSnackbar]);

  const buildFallbackActivity = React.useCallback(
    (apps: CongregationApplication[]): ActivityTimelineItem[] => {
      if (!apps.length) {
        return [];
      }
      const entries: ActivityTimelineItem[] = [];
      apps
        .slice()
        .sort((a, b) => {
          const dateA = a.updatedAt ?? a.createdAt ?? '';
          const dateB = b.updatedAt ?? b.createdAt ?? '';
          const valueA = dateA ? dayjs(dateA).valueOf() : -Infinity;
          const valueB = dateB ? dayjs(dateB).valueOf() : -Infinity;
          return valueB - valueA;
        })
        .slice(0, fallbackActivityLimit)
        .forEach((application) => {
          const timestamp = application.updatedAt ?? application.createdAt;
          if (!timestamp) {
            return;
          }
          entries.push({
            id: `application-${application.id}`,
            title: `Application #${application.id}`,
            description: `Status updated to ${getStatusLabel(application.status)}`,
            timestamp,
            contextLabel: application.assignedMentorName
              ? `Caretaker: ${application.assignedMentorName}`
              : undefined,
          });
        });
      return entries;
    },
    [],
  );

  const loadData = React.useCallback(async () => {
    const isFirstLoad = firstLoadRef.current;
    if (isFirstLoad) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    try {
      const params: CongregationAdminFilter = {};
      if (statusFilter && statusFilter !== 'PENDING') {
        params.status = statusFilter;
      }
      const [applicationsResponse, pipelineResponse] = await Promise.all([
        fetchCongregationAdminApplications(params),
        fetchCongregationAdminPipeline(params),
      ]);
      const fetchedApplications = applicationsResponse.applications ?? [];
      const filteredApplications =
        statusFilter === 'PENDING'
          ? fetchedApplications.filter((application) =>
            application.status === 'SUBMITTED' ||
            application.status === 'ASSIGNED_TO_MENTOR',
          )
          : fetchedApplications;
      setApplications(filteredApplications);
      setPipeline(pipelineResponse.pipeline ?? null);
    } catch (error) {
      console.error('Failed to load congregation dashboard', error);
      showSnackbar('Unable to load congregation applications.', 'danger');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
      firstLoadRef.current = false;
    }
  }, [showSnackbar, statusFilter]);

  React.useEffect(() => {
    loadMasters();
  }, [loadMasters]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  React.useEffect(() => {
    setActivityEvents(buildFallbackActivity(applications));
  }, [applications, buildFallbackActivity]);

  const mentorLoad = React.useMemo(() => {
    const counts = new Map<string, number>();
    applications.forEach((application) => {
      if (application.status === 'ASSIGNED_TO_MENTOR') {
        const mentorName = application.assignedMentorName ?? 'Unassigned caretaker';
        counts.set(mentorName, (counts.get(mentorName) ?? 0) + 1);
      }
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [applications]);

  const pipelineCards = React.useMemo(
    () => [
      {
        key: 'pending',
        label: 'Pending reviews',
        value: pipeline?.pending ?? (pipeline?.submitted ?? 0) + (pipeline?.assigned ?? 0),
        description: 'Applications awaiting caretaker action',
      },
      {
        key: 'submitted',
        label: 'Submitted',
        value: pipeline?.submitted ?? 0,
        description: 'Awaiting assignment',
      },
      {
        key: 'assigned',
        label: 'Assigned to caretakers',
        value: pipeline?.assigned ?? 0,
        description: 'Queued with caretakers',
      },
      {
        key: 'accepted',
        label: 'Accepted',
        value: pipeline?.accepted ?? 0,
        description: 'Approved into congregation',
      },
      {
        key: 'rejected',
        label: 'Rejected',
        value: pipeline?.rejected ?? 0,
        description: 'Declined with caretaker notes',
      },
      {
        key: 'withdrawn',
        label: 'Withdrawn',
        value: pipeline?.withdrawn ?? 0,
        description: 'Applicant withdrew request',
      },
    ],
    [pipeline],
  );

  const renderTimestamp = React.useCallback((value?: string | null) => {
    if (!value) {
      return '—';
    }
    const absolute = dayjs(value).format('DD MMM YYYY HH:mm');
    const relative = dayjs(value).fromNow();
    return (
      <Tooltip title={absolute}>
        <Typography level="body-xs" color="neutral">
          {relative}
        </Typography>
      </Tooltip>
    );
  }, []);

  const renderStatusChip = React.useCallback(
    (status: CongregationApplicationStatus) => (
      <Chip size="sm" variant="soft" color={statusColorMap[status] ?? 'neutral'}>
        {getStatusLabel(status)}
      </Chip>
    ),
    [],
  );

  const refreshButtonLoading = isRefreshing;

  const handleRefresh = React.useCallback(() => {
    loadData();
  }, [loadData]);

  return (
    <Box>
      <Stack spacing={3} sx={{ pb: 6 }}>
        <Card
          variant="outlined"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 'xl',
            borderColor: 'divider',
            boxShadow: 'lg',
            background: heroBackground,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: heroOverlay,
              opacity: 0.9,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: 180,
              height: 180,
              borderRadius: '50%',
              right: { xs: -60, md: -20 },
              top: { xs: 30, md: 10 },
              background: orbColor,
              filter: 'blur(6px)',
              animation: 'controlroom-float 8s ease-in-out infinite',
              '@keyframes controlroom-float': {
                '0%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-12px)' },
                '100%': { transform: 'translateY(0px)' },
              },
              pointerEvents: 'none',
            }}
          />
          <CardContent sx={{ position: 'relative' }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                <Stack spacing={0.75}>
                  <Typography level="h2" sx={{ fontWeight: 800, color: heroTitle }}>
                    Congregation control room
                  </Typography>
                  <Typography level="body-sm" sx={{ maxWidth: 760, color: heroBody }}>
                    Signed in as {membership.member.displayName}. Monitor application flow and mentor capacity across the congregation network.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip variant="soft" color="primary">
                    {pipelineSummary.pending} pending
                  </Chip>
                  <Chip variant="soft" color="success">
                    {pipelineSummary.accepted} accepted
                  </Chip>
                  <Chip variant="soft" color="danger">
                    {pipelineSummary.rejected} rejected
                  </Chip>
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelWarm,
                    boxShadow: 'sm',
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Intake pulse
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      {pipelineSummary.submitted} submitted • {pipelineSummary.assigned} assigned
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      {pipelineSummary.withdrawn} withdrawn across current filters.
                    </Typography>
                  </CardContent>
                </Card>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelCool,
                    boxShadow: 'sm',
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Quick actions
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                      <Button
                        variant="soft"
                        color="primary"
                        onClick={handleRefresh}
                        loading={refreshButtonLoading}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Refresh data
                      </Button>
                      <Button
                        variant="outlined"
                        color="neutral"
                        onClick={() => updateSearchParam('status', null)}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Clear status filter
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 'xl' }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography level="title-md">Filters</Typography>
              <Grid container spacing={2} columns={12}>
                <Grid xs={12} sm={6} md={4}>
                  <Stack spacing={1}>
                    <Typography level="body-xs" textTransform="uppercase" fontWeight={600}>
                      Application status
                    </Typography>
                    <Select
                      value={statusFilter}
                      onChange={(_, value) => updateSearchParam('status', value)}
                      placeholder="Select status"
                    >
                      {statusOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Stack>
                </Grid>
              </Grid>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="solid"
                  color="primary"
                  onClick={handleRefresh}
                  loading={refreshButtonLoading}
                >
                  Refresh data
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size="md" aria-label="Loading congregation applications" />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Grid container spacing={2} columns={12}>
              {pipelineCards.map((card) => (
                <Grid key={card.key} xs={12} sm={6} md={4} lg={2}>
                  <Card
                    variant={'outlined'}
                    color={'neutral'}
                    sx={{ borderRadius: 'xl', height: '100%' }}
                  >
                    <CardContent>
                      <Typography level="body-sm" textTransform="uppercase" fontWeight={600}>
                        {card.label}
                      </Typography>
                      <Typography level="h2">{card.value}</Typography>
                      <Typography level="body-xs" color="neutral">
                        {card.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2} columns={12}>
              <Grid xs={12} lg={6}>
                <Card variant="outlined" sx={{ borderRadius: 'xl', height: '100%' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Stack spacing={0.5}>
                      <Typography level="title-lg">Caretaker load</Typography>
                      <Typography level="body-sm" color="neutral">
                        Active application assignments by caretaker
                      </Typography>
                    </Stack>
                    {mentorLoad.length === 0 ? (
                      <Sheet
                        variant="soft"
                        color="neutral"
                        sx={{ borderRadius: 'lg', py: 4, textAlign: 'center' }}
                      >
                        <Typography level="body-sm">
                          No caretaker assignments match the selected filters.
                        </Typography>
                      </Sheet>
                    ) : (
                      <Table aria-label="Caretaker assignment load" size="sm">
                        <thead>
                          <tr>
                            <th>
                              <Typography level="body-xs" fontWeight={600} textTransform="uppercase">
                                Mentor
                              </Typography>
                            </th>
                            <th style={{ width: 120, textAlign: 'right' }}>
                              <Typography level="body-xs" fontWeight={600} textTransform="uppercase">
                                Assigned
                              </Typography>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {mentorLoad.map(([mentorName, count]) => (
                            <tr key={mentorName}>
                              <td>{mentorName}</td>
                              <td style={{ textAlign: 'right' }}>{count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid xs={12} lg={6}>
                <Card variant="outlined" sx={{ borderRadius: 'xl', height: '100%' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Stack spacing={0.5}>
                      <Typography level="title-lg">Application feed</Typography>
                      <Typography level="body-sm" color="neutral">
                        Latest submissions matching your filters
                      </Typography>
                    </Stack>
                    {applications.length === 0 ? (
                      <Sheet
                        variant="soft"
                        color="neutral"
                        sx={{ borderRadius: 'lg', py: 4, textAlign: 'center' }}
                      >
                        <Typography level="body-sm">No applications found.</Typography>
                      </Sheet>
                    ) : (
                      <Table aria-label="Congregation applications" size="sm" stickyHeader>
                        <thead>
                          <tr>
                            <th>
                              <Typography level="body-xs" fontWeight={600} textTransform="uppercase">
                                Application ID
                              </Typography>
                            </th>
                            <th>
                              <Typography level="body-xs" fontWeight={600} textTransform="uppercase">
                                Applicant
                              </Typography>
                            </th>
                            <th>
                              <Typography level="body-xs" fontWeight={600} textTransform="uppercase">
                                Status
                              </Typography>
                            </th>
                            <th>
                              <Typography level="body-xs" fontWeight={600} textTransform="uppercase">
                                Submitted
                              </Typography>
                            </th>
                            <th>
                              <Typography level="body-xs" fontWeight={600} textTransform="uppercase">
                                Assigned mentor
                              </Typography>
                            </th>
                            <th>
                              <Typography level="body-xs" fontWeight={600} textTransform="uppercase">
                                Updated
                              </Typography>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {applications.map((application) => (
                            <tr key={application.id}>
                              <td>
                                <Typography level="body-sm" fontWeight={600}>
                                  #{application.id}
                                </Typography>
                              </td>
                              <td>{application.applicantId}</td>
                              <td>{renderStatusChip(application.status)}</td>
                              <td>{renderTimestamp(application.submittedAt)}</td>
                              <td>{application.assignedMentorName ?? '—'}</td>
                              <td>{renderTimestamp(application.updatedAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 'xl', height: '100%' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Stack spacing={0.5}>
                      <Typography level="title-lg">Activity log</Typography>
                      <Typography level="body-sm" color="neutral">
                        Latest congregation application updates while the dedicated audit feed is unavailable.
                      </Typography>
                    </Stack>
                    <ActivityTimeline
                      items={activityEvents}
                      emptyMessage={
                        applications.length > 0
                          ? 'No recent application updates available.'
                          : 'No activity has been recorded yet.'
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
